import { useState } from 'react'
import { AskBennyPill } from '@/components/assistant/AskBennyButton'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useClientPortalAssistantCapabilities } from '@/hooks/useClientPortalAssistant'
import { useClientPortalSummary } from '@/hooks/useClientPortalSummary'
import { useLocation, useNavigate } from 'react-router-dom'

import { PortalBennyChat } from './PortalBennyChat'
import { usePortalBennySession } from './usePortalBennySession'

/**
 * Whether the portal Ask Benny pill should render here: the portal's Benny
 * switch is on, the server reports Benny enabled (the capabilities call 403s
 * when the org's AI flag is off), and this isn't the Benny page itself.
 * Hosts that wrap the pill in chrome (the desktop title-bar island) use this
 * to skip the wrapper too, so an empty pill never shows.
 */
export function usePortalAskBennyAvailable(entityId: string | undefined) {
  const { pathname } = useLocation()
  const summary = useClientPortalSummary(entityId)
  const showAssistant = summary.data?.settings?.showAssistant === true
  const capabilities = useClientPortalAssistantCapabilities(entityId, {
    enabled: showAssistant,
  })
  const onBennyPage = /\/benny\/?$/.test(pathname)
  return (
    !!entityId &&
    showAssistant &&
    capabilities.data?.enabled === true &&
    !onBennyPage
  )
}

/**
 * Ask Benny for a client portal user: the same pill as the org top bar, but
 * it opens the portal's own Benny (their conversations only) in a popover,
 * and Expand goes to the portal Benny page.
 *
 * Shown only when the portal's Benny switch is on AND the server reports Benny
 * enabled (the capabilities call 403s when the org's AI flag is off). Hidden
 * on the Benny page itself so there is one chat per screen. Staff previewing
 * the portal get a read-only chat; the API rejects their writes regardless.
 */
export function PortalAskBennyButton({
  entityId,
  className,
  labelClassName,
}: {
  entityId: string
  className?: string
  labelClassName?: string
}) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const summary = useClientPortalSummary(entityId)
  const isAvailable = usePortalAskBennyAvailable(entityId)
  const [sessionId, setSessionId] = usePortalBennySession(entityId)

  if (!isAvailable) return null

  const readOnly = summary.data?.isInternalViewer === true

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <AskBennyPill
                open={open}
                className={className}
                labelClassName={labelClassName}
              />
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={6}>
            {open ? 'Close Benny' : 'Ask Benny'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent
        aria-label="Benny assistant"
        align="end"
        sideOffset={8}
        padding="none"
        collisionPadding={12}
        className="rounded-card overflow-hidden border-border bg-background shadow-[var(--shadow-floating)]"
        style={{
          width: 'min(420px, calc(100vw - 24px))',
          height: 'min(560px, calc(100vh - 96px))',
        }}
      >
        <PortalBennyChat
          entityId={entityId}
          sessionId={sessionId}
          onSessionChange={setSessionId}
          variant="popover"
          readOnly={readOnly}
          onExpand={() => {
            setOpen(false)
            const query = sessionId
              ? `?session=${encodeURIComponent(sessionId)}`
              : ''
            navigate(
              `/client-portal/${encodeURIComponent(entityId)}/benny${query}`,
            )
          }}
          onClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  )
}
